// Frontend mirror of backend weather.py classification for client-side polish
// (e.g. when a forecast came back but we want to relabel based on selected job).

import { CloudRain, CloudDrizzle, Sun, Wind, CloudOff, Cloud, CloudSun, CloudFog, CloudLightning, CloudSnow } from "lucide-react";

const STYLES = {
  green: {
    cell: "bg-emerald-50 border-emerald-300 text-emerald-800",
    badge: "bg-emerald-600 text-white border-emerald-700",
    dot: "bg-emerald-500",
    icon: Sun,
  },
  amber: {
    cell: "bg-amber-50 border-amber-300 text-amber-800",
    badge: "bg-amber-400 text-zinc-900 border-amber-500",
    dot: "bg-amber-400",
    icon: CloudDrizzle,
  },
  red: {
    cell: "bg-red-50 border-red-300 text-red-800",
    badge: "bg-red-600 text-white border-red-700",
    dot: "bg-red-600",
    icon: CloudRain,
  },
  unknown: {
    cell: "bg-zinc-50 border-zinc-200 text-zinc-500",
    badge: "bg-zinc-100 text-zinc-600 border-zinc-300",
    dot: "bg-zinc-300",
    icon: CloudOff,
  },
};

// Picks a more specific icon when wind is the dominant factor.
export function weatherIcon(day) {
  if (!day) return CloudOff;
  if ((day.wind_kmh || 0) >= 40 && (day.rain_mm || 0) < 3) return Wind;
  return STYLES[day.severity]?.icon || Sun;
}

export function weatherStyles(severity) {
  return STYLES[severity] || STYLES.unknown;
}

// Icon + colour class from the WMO weather_code (falls back to severity icon).
export function weatherVisual(day) {
  if (!day) return { Icon: CloudOff, cls: "text-zinc-400" };
  if ((day.wind_kmh || 0) >= 40 && (day.rain_mm || 0) < 3) return { Icon: Wind, cls: "text-teal-500 dark:text-teal-400" };
  const c = day.weather_code;
  if (c === 0) return { Icon: Sun, cls: "text-amber-400" };
  if (c === 1 || c === 2) return { Icon: CloudSun, cls: "text-amber-300" };
  if (c === 3) return { Icon: Cloud, cls: "text-zinc-400" };
  if (c === 45 || c === 48) return { Icon: CloudFog, cls: "text-zinc-400" };
  if (c >= 51 && c <= 57) return { Icon: CloudDrizzle, cls: "text-sky-500 dark:text-sky-400" };
  if ((c >= 61 && c <= 67) || (c >= 80 && c <= 82)) return { Icon: CloudRain, cls: "text-sky-500 dark:text-sky-400" };
  if ((c >= 71 && c <= 77) || c === 85 || c === 86) return { Icon: CloudSnow, cls: "text-sky-300" };
  if (c >= 95) return { Icon: CloudLightning, cls: "text-purple-500 dark:text-purple-400" };
  const Icon = weatherIcon(day);
  return { Icon, cls: day.severity === "red" ? "text-red-500 dark:text-red-400" : "text-amber-400" };
}

export function dayShort(iso) {
  if (!iso) return "—";
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString("en-NZ", { weekday: "short", day: "numeric" });
}

// "Tomorrow, 16 Jun"
export function dayLong(iso) {
  if (!iso) return "—";
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString("en-NZ", { weekday: "long", day: "numeric", month: "short" });
}
